import { AxiosResponse } from "axios";
import { IAbstractModel } from "model/abstract.model";
import { IConsumidor } from "model/cliente.model";
import { Dispatch, SetStateAction } from "react";
import { StatusContaPagarReceber } from "pages/financas/financas.model";


export const currencyOf = (value: number | null = 0) => {
    return (value ?? 0).toLocaleString('pt-br', { style: 'currency', currency: 'BRL' });
};

// xs, extra-small: 0px
// sm, small: 600px
// md, medium: 900px
// lg, large: 1200px
// xl, extra-large: 1536px

export const isLayoutXs = () => {
    return window.innerWidth < 600;
};

export const isLayoutSm = () => {
    return window.innerWidth >= 600 && window.innerWidth < 900;
};

export const isLayoutMd = () => {
    return window.innerWidth >= 900 && window.innerWidth < 1200;
};

export const isLayoutLg = () => {
    return window.innerWidth >= 1200 && window.innerWidth < 1536;
};

export const isLayoutXl = () => {
    return window.innerWidth >= 1536;
};

export const fakeAxiosPromise = <T>(value: T, timeMin: number, timeMax: number): Promise<AxiosResponse<T>> => {
    const minCeiled = Math.ceil(timeMin);
    const maxFloored = Math.floor(timeMax);
    const delay = Math.floor(Math.random() * (maxFloored - minCeiled + 1) + minCeiled);
    return new Promise((resolve, reject) => {
        const toReturn: AxiosResponse<T> = {
            data: value,
        } as AxiosResponse<T>;
        setTimeout(() => {
            resolve(toReturn);
        }, delay);
    });
};

/**
 * Remove os zeros após a vírgula de uma string, se todos os dígitos após a vírgula forem zeros.
 *
 * @param {string} str - A string de entrada a ser limpa.
 * @returns {string} A string limpa, ou a string original se não houver zeros após a vírgula.
 */
export const limpaRef = (str: string) => {
    // eslint-disable-next-line
    const [, parteDecimal] = str.match(/,(\d*)/) || [, ''];
    if (/^0*$/.test(parteDecimal)) {
        return str.replace(/,0*$/, '');
    } else {
        return str;
    }
};

export const limpaString = (str: string) => {
    return str.replace(/\D/g, '');
};

export const convertDateTime = (inputDate: string) => {
    const dateObject = new Date(inputDate);

    const day = dateObject.getDate().toString().padStart(2, '0');
    const month = (dateObject.getMonth() + 1).toString().padStart(2, '0');
    const year = dateObject.getFullYear();
    const hours = (dateObject.getHours()).toString().padStart(2, '0');
    const minutes = dateObject.getMinutes().toString().padStart(2, '0');
    const seconds = dateObject.getSeconds().toString().padStart(2, '0');

    return `${day}/${month}/${year} - ${hours}:${minutes}:${seconds}`;

};

/**
 * Arredonda um número para duas casas decimais e o converte em um número de ponto flutuante.
 *
 * @param {number} value - O número que deve ser arredondado.
 * @returns {number} O número arredondado com duas casas decimais.
 */

export const toFixed = (value: number): number => {
    return parseFloat(value.toFixed(2));
};

/**
 * Verifica se dois objetos são iguais, comparando suas representações JSON.
 *
 * @param {any} obj1 - O primeiro objeto a ser comparado.
 * @param {any} obj2 - O segundo objeto a ser comparado.
 * @returns {boolean} Retorna true se os objetos forem iguais, caso contrário, retorna false.
 *
 * @example
 * const obj1 = { nome: 'Alice', idade: 30 };
 * const obj2 = { nome: 'Alice', idade: 30 };
 * const saoIguais = equalsObj(obj1, obj2);
 * console.log(saoIguais); // true
 */
export const equalsObj = (obj1: any, obj2: any): boolean => {
    return JSON.stringify(obj1) === JSON.stringify(obj2);
};

/**
 * Capitaliza uma string ou as primeiras letras de todas as palavras em uma string.
 *
 * @param {string} string - A string a ser capitalizada.
 * @param {Object} opt - Opções para controle do processo de capitalização.
 * @param {boolean} opt.firstLetterOnly - Se true, apenas a primeira letra da string será capitalizada.
 *                                       Se false ou undefined, a primeira letra de cada palavra será capitalizada.
 * @param {boolean} opt.defaultCaptalize - Se false, as letras serão capitalizadas de forma invertida.
 *                                         Se true ou undefined, as letras serão capitalizadas normalmente.
 * @returns {string} - A string capitalizada.
 *
 * @example
 * const minhaString = "hello world";
 * const stringCapitalizada = capitalizeString(minhaString, { firstLetterOnly: true, defaultCaptalize: true });
 * console.log(stringCapitalizada); // Saída: Hello world
 *
 * @example
 * const minhaOutraString = "hello world";
 * const stringComPalavrasCapitalizadas = capitalizeString(minhaOutraString, { firstLetterOnly: false, defaultCaptalize: true });
 * console.log(stringComPalavrasCapitalizadas); // Saída: Hello World
 */
export const captalizeString = (string: string, opt?: { firstLetterOnly: boolean, defaultCaptalize: boolean; }) => {
    const captalize = (str: string) => {
        if (opt?.defaultCaptalize === false) {
            const stringUpperCase = str.toUpperCase();
            return stringUpperCase.charAt(0).toLowerCase() + stringUpperCase.slice(1);
        }
        const stringLowerCase = str.toLowerCase();
        return stringLowerCase.charAt(0).toUpperCase() + stringLowerCase.slice(1);
    };

    if (opt?.firstLetterOnly !== false) {
        return captalize(string);
    }

    const words = string.split(' ');
    const capitalizedWords = words.map(word => {
        if (word.length > 0) {
            return captalize(word);
        }
        return word;
    });

    return capitalizedWords.join(' ');
};

export const showOnlyDate = (inputDate: string | Date) => {
    const dateObject = new Date(inputDate);

    const day = dateObject.getUTCDate().toString().padStart(2, '0');
    const month = (dateObject.getUTCMonth() + 1).toString().padStart(2, '0');
    const year = dateObject.getUTCFullYear();

    return `${day}/${month}/${year}`;
};

export const showOnlyDateTwoDigts = (inputDate: string) => {
    const dateObject = new Date(inputDate);

    const day = dateObject.getUTCDate().toString().padStart(2, '0');
    const month = (dateObject.getUTCMonth() + 1).toString().padStart(2, '0');
    const year = dateObject.getUTCFullYear().toString().slice(-2);

    return `${day}/${month}/${year}`;
};

export const convertRelatorioDate = (inputDate: string) => {
    const day = inputDate.padStart(2, "0").substring(8);
    const month = inputDate.padStart(2, "0").substring(5, 7);
    const year = inputDate.padStart(2, "0").substring(0, 4);
    return `${day}/${month}/${year}`;
};

/**
 * Cria uma cópia profunda do objeto fornecido usando serialização e análise JSON.
 *
 * @template T
 * @param {T} obj - O objeto a ser copiado profundamente.
 * @returns {T} - Um novo objeto que é uma cópia profunda do objeto de entrada.
 * @throws {SyntaxError} Se o objeto de entrada não puder ser serializado e analisado usando JSON.
 *
 * @example
 * const objetoOriginal = { chave: 'valor', aninhado: { chaveInterna: 'valorInterno' } };
 * const objetoCopiado = deepCopy(objetoOriginal);
 * console.log(objetoCopiado); // Saída: { chave: 'valor', aninhado: { chaveInterna: 'valorInterno' } }
 */
export const deepCopy = <T>(obj: T): T => {
    return JSON.parse(JSON.stringify(obj)) as T;
};

/**
 * Cria uma cópia profunda (deep copy) de um objeto, incluindo suporte para:
 * - Objetos normais
 * - Arrays
 * - Funções (mantém a referência original)
 * - Instâncias de `Date`
 * - Estruturas `Map` e `Set`
 *
 * Também evita problemas com referências circulares utilizando `WeakMap`.
 *
 * ⚡ Nota:
 * - Funções são preservadas por referência (não clonadas).
 * - Objetos não suportados como `Promise`, `WeakMap` e `WeakSet` são ignorados (não tratados).
 *
 * @template T O tipo do objeto a ser copiado.
 * @param obj O objeto que será copiado profundamente.
 * @param _weakMap [Interno] Um `WeakMap` usado para armazenar referências já clonadas e evitar loops infinitos em estruturas circulares.
 * @returns Uma nova instância profundamente clonada do objeto original.
 *
 * @example
 * const original = {
 *   date: new Date(),
 *   map: new Map([['key', 'value']]),
 *   set: new Set([1, 2, 3]),
 *   nested: { x: 1 },
 *   func: () => true
 * };
 *
 * const copy = deepClone(original);
 * console.log(copy.date instanceof Date); // true
 * console.log(copy.map.get('key')); // 'value'
 * console.log(copy.set.has(2)); // true
 * console.log(copy.nested.x); // 1
 * console.log(copy.func()); // true
 */
export const deepClone = <T>(obj: T, _weakMap = new WeakMap()): T => {
    if (obj === null || typeof obj !== "object") {
        return obj;
    }

    // Evitar loops infinitos (circular reference)
    if (_weakMap.has(obj as object)) {
        return _weakMap.get(obj as object);
    }

    // Date
    if (obj instanceof Date) {
        return new Date(obj.getTime()) as unknown as T;
    }

    // Map
    if (obj instanceof Map) {
        const mapCopy = new Map();
        obj.forEach((value, key) => {
            mapCopy.set(key, deepClone(value, _weakMap));
        });
        return mapCopy as unknown as T;
    }

    // Set
    if (obj instanceof Set) {
        const setCopy = new Set();
        obj.forEach((value) => {
            setCopy.add(deepClone(value, _weakMap));
        });
        return setCopy as unknown as T;
    }

    // Array
    if (Array.isArray(obj)) {
        const arrCopy = obj.map((item) => deepClone(item, _weakMap));
        return arrCopy as unknown as T;
    }

    // Objeto normal
    const clonedObj = {} as any;
    _weakMap.set(obj as object, clonedObj);

    Object.keys(obj).forEach((key) => {
        const value = (obj as any)[key];
        clonedObj[key] = deepClone(value, _weakMap);
    });

    return clonedObj;
};

/**
 * Reseta as propriedades do objeto de estado fornecido para os valores no estado inicial.
 *
 * @template T - O tipo do objeto de estado.
 * @param {T} state - O objeto de estado a ser resetado.
 * @param {T} initialState - O estado inicial para resetar o objeto.
 * @returns {T} - O objeto de estado modificado após o reset.
 *
 * @example
 * // Exemplo de uso:
 * const meuEstado = { prop1: 'valor1', prop2: 'valor2' };
 * const estadoInicial = { prop1: 'valorInicial1', prop2: 'valorInicial2' };
 * const novoEstado = resetState(meuEstado, estadoInicial);
 * console.log(novoEstado); // Saída: { prop1: 'valorInicial1', prop2: 'valorInicial2' }
 */
export const deepReset = <T extends Record<string, any>>(state: T, initialState: T): T => {
    for (let prop in state) {
        delete state[prop as keyof T];
    }
    return Object.assign(state, initialState);
};

/**
 * Trunca um número dado para um número especificado de casas decimais sem arredondamento.
 *
 * Esta função multiplica o número de entrada por 10 elevado ao número especificado
 * de casas decimais, trunca o resultado para um inteiro usando `Math.floor`,
 * e depois divide de volta para atingir o número desejado de casas decimais.
 *
 * @param {number} numero - O número a ser truncado.
 * @param {number} casasDecimais - O número de casas decimais a manter.
 * @returns {number} O número truncado com o número especificado de casas decimais.
 *
 * @example
 * // Truncar 5.6789 para 2 casas decimais
 * const resultado = cortarCasasDecimais(5.6789, 2); // resultado é 5.67
 *
 * @example
 * // Truncar -3.4567 para 3 casas decimais
 * const resultado = cortarCasasDecimais(-3.4567, 3); // resultado é -3.456
 */
export const cortarCasasDecimais = (numero: number, casasDecimais: number): number => {
    const multiplicador = Math.pow(10, casasDecimais);
    const numeroCortado = Math.floor(numero * multiplicador) / multiplicador;
    return numeroCortado;
};

export const roundToTwoDecimalPlaces = (value: number) => {
    const fixedNumber = value.toFixed(10);  // Use a larger number to ensure precision
    const roundedNumber = parseFloat(fixedNumber);
    return Number(roundedNumber);  // Convert the formatted string back to a number
};

/**
 * Arredonda um número para o modo "round half to even" (bankers rounding).
 *
 * @param {number} value - O número a ser arredondado.
 * @param {number} decimalPlaces - O número de casas decimais para arredondar.
 * @returns {number} - O número arredondado.
 *
 * @description
 * Arredonda para o vizinho mais próximo, a menos que ambos os vizinhos sejam equidistantes.
 * Nesse caso, arredonda para o vizinho par. Esse é o modo de arredondamento que minimiza
 * o erro acumulativo quando aplicado repetidamente sobre uma sequência de cálculos.
 *
 * @example
 * console.log(roundHalfToEven(3.5, 0)) // 4
 * console.log(roundHalfToEven(2.5, 0)) // 2
 * console.log(roundHalfToEven(19.905, 2)) // 19.90
 */
export const roundHalfToEven = (value: number, decimalPlaces: number = 2): number => {
    const factor = 10 ** decimalPlaces;
    const valueFactor = cortarCasasDecimais(value * factor, 2);
    const hasExactlyDot5 = valueFactor % 1 === 0.5;
    const roundedValue = Math.round(value * factor);
    const isOdd = roundedValue % 2 !== 0;
    if (hasExactlyDot5 && isOdd) {
        return (roundedValue - 1) / factor;
    }
    return roundedValue / factor;
};
/**
 * Limita um valor dentro de um intervalo especificado.
 *
 * @param value - O valor a ser limitado.
 * @param min - O valor mínimo no intervalo.
 * @param max - O valor máximo no intervalo.
 * @returns O valor limitado dentro do intervalo especificado.
 */
export const clamp = (value: number, min: number, max: number): number => {
    // Math.max(min, Math.min(max, value)) assegura que o valor seja no mínimo min
    // e no máximo max. Se o valor for menor que min, é ajustado para min. Se o valor
    // for maior que max, é ajustado para max.
    return Math.max(min, Math.min(max, value));
};

/**
 * Ordena um array de objetos ou valores primitivos alfanumericamente.
 * OBS: ordena números baseado na ordem natural. Ex: 1, 2, 11, 12.
 *
 * @template T - O tipo de elementos no array.
 * @param {Array<T>} array - O array a ser ordenado.
 * @param {Object} [opt] - Opções de ordenação.
 * @param {string | Array<string>} [opt.fieldToSort] - O nome(s) do(s) campo(s) a ser(em) usado(s) para ordenação (aplicável apenas a arrays de objetos).
 * @param {'ASC' | 'DESC'} [opt.orderDirection='ASC'] - A direção da ordenação ('ASC' para ascendente, 'DESC' para descendente).
 * @throws {Error} Se `fieldToSort` não for fornecido para um array de objetos.
 * @returns {Array<T>} O array ordenado.
 *
 * @example
 * const arrObj = [
 *   { pessoa: { nome: 'Artico', idade: 23 }},
 *   { pessoa: { nome: 'Parisi', idade: 17 }}
 * ];
 * console.log(sortArray(arrObj, { fieldToSort: 'pessoa.idade' )
 * // result = [
 * //   { pessoa: { nome: 'Parisi', idade: 17 }},
 * //   { pessoa: { nome: 'Artico', idade: 23 }}
 * // ]
 *
 * const arrObj2 = [
 *   { pessoa: { nome: 'Maria', idade: 25 }},
 *   { pessoa: { nome: 'Maria', idade: 20 }},
 *   { pessoa: { nome: 'João', idade: 31 }}
 * ];
 * console.log(sortArray(arrObj2, { fieldToSort: [ 'pessoa.nome', 'pessoa.idade'] )
 * // result = [
 * //   { pessoa: { nome: 'João', idade: 31 }},
 * //   { pessoa: { nome: 'Maria', idade: 20 }},
 * //   { pessoa: { nome: 'Maria', idade: 25 }},
 * // ]
 *
 */
export const sortArray = <T extends Record<string, any> | string | number>(
    array: T[],
    opt?: {
        fieldToSort?: string[] | string;
        orderDirection?: OrderSpecifier;
        nullableFirst?: boolean;
    },
): T[] => {
    const isObjArray = typeof array[0] === 'object';
    if (isObjArray && (opt?.fieldToSort === undefined || opt.fieldToSort.length === 0)) {
        throw new Error('Deve informar fieldsToSort se o array for de objeto');
    }
    const fildToSort = opt?.fieldToSort ?? '';
    const fields = Array.isArray(fildToSort) ? fildToSort : Array.of(fildToSort);
    const orderDirection = opt?.orderDirection || 'ASC';
    const nullableFirst = opt?.nullableFirst ?? false;

    const compareFunction = (a: any, b: any): number => {
        let comparison = 0;
        for (let i = 0; i < fields.length; i++) {
            const field = fields[i];
            const valueA = isObjArray ? resolve(a, field) : a;
            const valueB = isObjArray ? resolve(b, field) : b;

            if (!valueA) {
                comparison =
                    !valueB ? 0
                        : nullableFirst ? -1
                            : 1;
            } else if (!valueB) {
                comparison = nullableFirst ? 1 : -1;
            } else {
                const collator = new Intl.Collator(undefined, {
                    numeric: true,
                    sensitivity: 'base',
                });
                comparison = collator.compare(valueA.toString(), valueB.toString());
            }

            if (comparison !== 0) break;
        }
        return orderDirection === 'DESC' ? -comparison : comparison;
    };

    return deepCopy(array).sort(compareFunction);
};

export type OrderSpecifier = 'ASC' | 'DESC';

export type SortOpt = {
    orderBy: string;
    orderDirection?: OrderSpecifier;
};

export const invertOrderDirection = (order?: OrderSpecifier) => {
    return !order || order === 'ASC' ? 'DESC' : 'ASC';
};

/**
 * Converte uma string ou objeto Date para uma string representando uma data no formato pt-BR 'dd/MM/yyyy'.
 * @param {string | Date} string - A string ou objeto Date a ser convertido.
 * @returns {string} A data no formato local.
 *
 * @example
 * console.log(toLocaleDateString());                            // new Date() 'dd/MM/yyyy'
 * console.log(toLocaleDateString('2024-03-11'));                // '11/03/2024'
 * console.log(toLocaleDateString('2001-03-20T23:59:50-03:00')); // '20/03/2001'
 * console.log(toLocaleDateString('2024-03-12T23:59:59.999z'));  // '12/03/2024'
 */
export const toLocaleDateString = (string: string | Date = new Date()): string => {
    if (!(string instanceof Date) && string.length === 10) {
        return convertRelatorioDate(string);
    }
    const date = new Date(string);
    return date.toLocaleDateString('pt-BR');
};


export const toLocaleDateTimeString = (isoString: string | Date = new Date()): string => {
    const date = new Date(isoString);

    const dateOptions: Intl.DateTimeFormatOptions = {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
    };

    const timeOptions: Intl.DateTimeFormatOptions = {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false, // Use 24-hour format
    };

    const formattedDate = date.toLocaleDateString('pt-BR', dateOptions);
    const formattedTime = date.toLocaleTimeString('pt-BR', timeOptions);

    return `${formattedDate} às ${formattedTime}`;
};


/**
 * Converte uma data em formato string ou objeto Date para uma string no formato 'YYYY-MM-DD',
 * adequada para envio para o backend (LocalDate).
 * @param {string | Date} date A data para ser convertida. Pode ser uma string no formato de data válido ou um objeto Date.
 * @returns {string} Uma string no formato 'YYYY-MM-DD' representando a data fornecida.
 */
export const toLocalDateBackEndFriendly = (date: string | Date): string => {
    return new Date(date).toISOString().split("T")[0];
};

/**
 * Verifica se uma data ocorre antes de outra data.
 * @param {string | Date} date1 - A primeira data a ser comparada.
 * @param {Date} [date2=new Date()] - A segunda data a ser comparada. Se não fornecido, será a data atual.
 * @returns {boolean} Retorna verdadeiro se `date1` ocorrer antes de `date2`, caso contrário, retorna falso.
 */

export const getByTitle = (title: string) => {
    switch (title) {
        case "CREDITOLOJA":
            return "Crédito da loja";
        case "CREDIARIO":
            return "Crediário";
        case "DEPOSITOBANCARIO":
            return "Depósito bancário";
        default: return title;
    }
};

export const isConsumidor = (cliente: IAbstractModel | IConsumidor): cliente is IConsumidor => {
    return (cliente as IConsumidor).tipoPessoa !== undefined;
};

export const extrairNumeroInfoAdicionalDescritivo = (texto: string | null): number | null => {
    if (!texto) return null;
    // Usando expressão regular para encontrar o número
    const match: RegExpMatchArray | null = texto.match(/\d+/);

    if (match) {
        // Convertendo o número encontrado para inteiro
        return parseInt(match[0], 10);
    } else {
        // Retorna null se nenhum número for encontrado
        return null;
    }
}

export const isValidEmail = (email: string): boolean => {
    // Check if email is empty or contains spaces
    if (email.trim().length === 0 || email.includes(' ')) {
        return false;
    }

    let somaArroba = 0;
    const specialCharacters = "áéóã!#$%¨&*()<>/|*=:;,^~+-?°ºª[]{}";

    for (let i = 0; i < email.length; i++) {
        if (email[i] === '@') {
            somaArroba += 1;
        }
        if (specialCharacters.includes(email[i])) {
            return false;
        }
    }

    // Check if there is exactly one '@' symbol
    if (somaArroba !== 1) {
        return false;
    }

    // Check if '@' is not the last character and there's at least one '.'
    if (email.indexOf('@') === email.length - 1 || !email.includes('.')) {
        return false;
    }

    return true;
};

export const formatPhoneNumber = (number: string): string => {
    const cleaned = limpaString(number);
    if (cleaned.length === 11) {
        return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 7)}-${cleaned.slice(7)}`;
    } else if (cleaned.length === 10) {
        return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 6)}-${cleaned.slice(6)}`;
    }
    return number;
}


export const isAtivo = (str: string | undefined): boolean => {
    if (!str) return false;
    if (str.toUpperCase() === "ATIVO") return true
    return false
}

/**
 * Checks if a string separated by spaces is in order within
 * another string ignoring case.
 * @param {string} baseString - String to be checked
 * @param {string} toSearch - String to be searched
 * @returns {boolean}
 * @example
 * const baseString = 'Hello world!';
 * includesStringInOrder(baseString, 'hEl wOrl'); --------> true
 * includesStringInOrder(baseString, '   hel  worl  '); --> true
 * includesStringInOrder(baseString, 'worl hel'); --------> false
 */
export function includesStringInOrder(baseString: string, toSearch: string = ''): boolean {
    const toSearchItens: string[] = replaceWhiteSpaces(toSearch.toLowerCase(), ' ').split(' ');
    const normalizedBaseString = baseString.toLowerCase();

    let baseIndex = 0;
    for (const i of toSearchItens) {
        const foundIndex = normalizedBaseString.indexOf(i, baseIndex);
        if (foundIndex === -1) {
            return false;
        }
        baseIndex = foundIndex + 1;
    }
    return true;
}

export function replaceWhiteSpaces(oldValue: string, toReplace: string): string {
    return oldValue.replace(/\s+/g, toReplace).trim();
}

/**
 * Verifica se há objetos duplicados em um array de objetos complexos.
 *
 * @template T - O tipo de objetos contidos no array.
 * @param {T[]} arr - O array de objetos a ser verificado se existem duplicatas.
 * @param {CompareFn<T>} [compareFunction] - Função opcional de comparação para
 * verificar se dois itens são iguais.
 * @returns {HasDuplicateResult<T>} - Um objeto contendo:
 *   - `hasDuplicated` (boolean): Indica se há itens duplicados.
 *   - `itensChecados` (T[]): Um array de objetos verificados, sem duplicatas.
 *   - `itensDuplicados` (T[]): Um array de objetos duplicados encontrados.
 *
 * @example
 * const array = [
 *   { id: 1, nome: 'Ana' },
 *   { id: 2, nome: 'Bruno' },
 *   { id: 3, nome: 'Carlos' },
 *   { id: 3, nome: 'João' },
 *   { id: 2, nome: 'Bruno' } // Duplicata
 * ];
 * const resultado = hasDuplicateObjects(array);
 * console.log(resultado);
 * // { hasDuplicated: true,
 * //   itensChecados: [
 * //      { id: 1, nome: 'Ana' },
 * //      { id: 2, nome: 'Bruno' },
 * //      { id: 3, nome: 'Carlos' },
 * //      { id: 3, nome: 'João' },
 * //   ],
 * //   itensDuplicados: [
 * //      { id: 2, nome: 'Bruno' }
 * //   ]
 * // }
 *
 * @example
 * // Define uma função de comparação personalizada
 * const compareFunction = (
 *      item1: { id: number; nome: string; },
 *      item2: { id: number; nome: string; }
 *  ) => { return item1.id === item2.id; };
 *
 * // Verifica se há duplicatas no array usando a função de comparação personalizada
 * const resultadoComFuncao = hasDuplicateObjects(array, compareFunction);
 * console.log(resultadoComFuncao);
 * // {
 * //   hasDuplicated: true,
 * //   itensChecados: [
 * //       { id: 1, nome: 'Ana' },
 * //       { id: 2, nome: 'Bruno' },
 * //       { id: 3, nome: 'Carlos' }
 * //   ],
 * //   itensDuplicados: [
 * //       { id: 2, nome: 'Bruno' },
 * //       { id: 3, nome: 'João' },
 * //   ]
 * // };
 */
export const hasDuplicateObjects = <T extends {}>(
    arr: T[],
    compareFunction?: CompareFn<T>,
): HasDuplicateResult<T> => {
    const itensChecados: T[] = [];
    const itensDuplicados: T[] = [];
    for (const item of Array.from(arr)) {
        const isDuplicado = itensChecados.find(existingItem => {
            return compareFunction?.(existingItem, item) ?? equalsObj(existingItem, item);
        });
        if (!isDuplicado) {
            itensChecados.push(item);
        } else if (!itensDuplicados.find(i => compareFunction?.(i, item) ?? equalsObj(i, item))) {
            itensDuplicados.push(item);
        }
    }
    return { hasDuplicated: itensDuplicados.length > 0, itensChecados, itensDuplicados };
};

export type CompareFn<T extends {}> = (item1: T, item2: T) => boolean;
export type HasDuplicateResult<T extends {}> = {
    hasDuplicated: boolean;
    itensChecados: T[];
    itensDuplicados: T[];
};


/**
 * Compara dois arrays e retorna os elementos que estão presentes apenas em um dos arrays,
 * com base em uma função de igualdade opcional. Caso a função `equals` não seja fornecida,
 * será usada a função `equalsObj` como fallback.
 *
 * @template T O tipo dos elementos dos arrays.
 * @param {T[]} arr1 - O primeiro array a ser comparado.
 * @param {T[]} arr2 - O segundo array a ser comparado.
 * @param {(a: T, b: T) => boolean} [equals] - Função opcional que determina se dois elementos são iguais.
 * @returns {T[]} Um novo array contendo os elementos que estão apenas em `arr1` ou apenas em `arr2`.
 *
 * @example
 * const a = [{ id: 1 }, { id: 2 }];
 * const b = [{ id: 2 }, { id: 3 }];
 * const diff = getDifferences(a, b, (x, y) => x.id === y.id);
 * // diff: [{ id: 1 }, { id: 3 }]
 *
 * @example
 * // Usando comparação padrão (equalsObj) caso `equals` não seja fornecido.
 * const diff = getDifferences([{ a: 1 }], [{ a: 1 }]);
 */
export const getDifferences = <T>(
    arr1: T[],
    arr2: T[],
    equals?: (a: T, b: T) => boolean
): T[] => {
    const equalsInterno = (a: T, b: T) => equals?.(a, b) ?? equalsObj(a, b);
    const onlyInArr1 = arr1.filter((a) => !arr2.some((b) => equalsInterno(a, b)));
    const onlyInArr2 = arr2.filter((b) => !arr1.some((a) => equalsInterno(a, b)));
    return [...onlyInArr1, ...onlyInArr2];
};

/**
 * Agrupa e mescla os itens de um array com base em uma chave definida pelo usuário.
 *
 * Esse método permite agrupar elementos de um array de acordo com uma chave personalizada (`keyFn`)
 * e, para elementos com a mesma chave, aplicar uma função de mesclagem (`mergeFn`) para combiná-los.
 *
 * @template T - O tipo dos itens no array.
 *
 * @param arr - O array de entrada contendo os itens a serem agrupados e mesclados.
 * @param opt - Um objeto contendo as funções `keyFn` e `mergeFn`:
 *
 * - `keyFn`: função que recebe um item e retorna a chave usada para agrupamento (string ou number).
 * - `mergeFn`: função que recebe o item agregado e o item atual, e retorna o novo item agregado.
 *
 * @returns Um novo array contendo os itens agrupados e mesclados.
 *
 * @example
 * const produtos = [
 *   { id: 'A', nome: 'Arroz', quantidade: 2 },
 *   { id: 'B', nome: 'Feijão', quantidade: 3 },
 *   { id: 'A', nome: 'Arroz', quantidade: 1 },
 * ];
 *
 * const agrupados = groupAndMergeArrayItens(produtos, {
 *   keyFn: item => item.id,
 *   mergeFn: (a, b) => ({ ...a, quantidade: a.quantidade + b.quantidade })
 * });
 *
 * // Resultado:
 * // [
 * //   { id: 'A', nome: 'Arroz', quantidade: 3 },
 * //   { id: 'B', nome: 'Feijão', quantidade: 3 }
 * // ]
 */
export const groupAndMergeArrayItens = <T>(
    arr: T[],
    opt: {
        keyFn: (item: T) => string | number;
        mergeFn: (aggregated: T, current: T) => T;
    }
) => {
    const map = new Map<string | number, T>();

    arr.forEach((item) => {
        const key = opt.keyFn(item);
        if (map.has(key)) {
            const merged = opt.mergeFn(map.get(key)!, item);
            map.set(key, merged);
        } else {
            map.set(key, item);
        }
    });

    return Array.from(map.values());
};

export const isEmpty = (empty: string | Array<any> | undefined): boolean => {
    if (empty === undefined) return true
    return empty.length === 0;
}

/**
 * Gera todas as possíveis chaves aninhadas de um objeto como strings, separadas por ponto (`.`).
 * Obs: Faz sentido se usado com o `resolve`...
 *
 * - Se `OnlyLeaf` for `true` (padrão), inclui apenas caminhos até propriedades "folha" (primitivos).
 * - Se `OnlyLeaf` for `false`, inclui também caminhos intermediários (objetos).
 * - Suporta arrays, incluindo caminhos como `array.${number}.propriedade` ou `array.${number}`.
 *
 * @template T - Tipo do objeto base
 * @template OnlyLeaf - Define se apenas folhas devem ser retornadas (default: true)
 *
 * @example
 * type Example = {
 *   usuario: {
 *     nome: string;
 *     contatos: { numero: string }[];
 *     apelidos: string[];
 *   };
 * };
 *
 * DotNestedKeys<Example> // "usuario.nome" | "usuario.contatos.${number}.numero" | "usuario.apelidos.${number}"
 */
export type DotNestedKeys<T, OnlyLeaf extends boolean = true> = {
    [K in keyof T & string]: T[K] extends (infer U)[]
    ? U extends object
    ? OnlyLeaf extends true
    ? `${K}.${number}.${DotNestedKeys<U, OnlyLeaf>}`
    : K | `${K}.${number}` | `${K}.${number}.${DotNestedKeys<U, OnlyLeaf>}`
    : `${K}.${number}`
    : T[K] extends object
    ? OnlyLeaf extends true
    ? `${K}.${DotNestedKeys<T[K], OnlyLeaf>}`
    : K | `${K}.${DotNestedKeys<T[K], OnlyLeaf>}`
    : K;
}[keyof T & string];

/**
 * Acessa dinamicamente uma propriedade aninhada de um objeto com base em um caminho em notação de ponto.
 *
 * O caminho é validado em tempo de compilação com base no tipo `T` e pode ser configurado para aceitar apenas caminhos de folhas
 * (valores finais, como `string`, `number`, etc.) ou também caminhos intermediários (objetos/arrays).
 *
 * Suporta também acesso a arrays via índice (`obj.lista.0.nome`), sendo interpretado como número.
 *
 * @template T - Tipo base do objeto a ser acessado.
 * @template OnlyLeaf - Se o caminho deve aceitar apenas propriedades terminais ("folhas"). Por padrão, é `true`.
 *
 * @param obj - O objeto de origem que será acessado.
 * @param path - Caminho da propriedade a ser acessada, no formato `"prop1.prop2.${index}.propFinal"`.
 *               O tipo desse caminho é restrito pelo tipo `T` e pelo valor de `OnlyLeaf`.
 * @param opt - Objeto de opções. Use `{ onlyLeaf: false }` para permitir caminhos intermediários.
 *
 * @returns O valor correspondente ao caminho informado ou `undefined` se algum nível for `null` ou `undefined`.
 *
 * @example
 * const data = {
 *   user: {
 *     name: "Ana",
 *     emails: [
 *       "ana3124@hotmail.com",
 *       "ana_novo@gmail.com",
 *     ],
 *     contacts: [
 *       { phone: "1234" },
 *       { phone: "5678" }
 *     ],
 *   }
 * };
 *
 * // Acessando uma propriedade folha (OnlyLeaf = true)
 * resolve(data, "user.name"); // "Ana"
 *
 * // Acessando dentro de um array de objetos
 * resolve(data, "user.contacts.1.phone"); // "5678"
 *
 * // Acessando dentro de um array simples
 * resolve(data, "user.emails.1"); // "ana_novo@gmail.com"
 *
 * // Acessando uma propriedade folha (OnlyLeaf = true)
 * resolve(data, "user.contacts.0.phone"); // "1234"
 *
 * // Acessando uma propriedade intermediária (OnlyLeaf = false)
 * resolve(data, "user.contacts", { onlyLeaf: false }); // [{ phone: "1234" }, { phone: "5678" }]
 */
export const resolve = <
    T extends Record<string, unknown>,
    OnlyLeaf extends boolean = true
>(
    obj: T,
    path: DotNestedKeys<T, OnlyLeaf>,
    opt?: {
        showOnlyLeafOptions?: OnlyLeaf;
    }
): any => {
    return path.split(".").reduce((acc, curr) => (acc as T)?.[curr] as T, obj);
};

export type ColumnData<T> = {
    id: string;
    key: DotNestedKeys<T>;
    header: string;
    type?: "string" | "number" | "options" | "checkBox" | "icon" | "custom" | "currency" | "unidadeMedida" | "percent";
    minWidth?: string;
    maxWidth?: number;
    width: number | string;
    textAlign?: "center" | "left" | "right";
    unidadeMedida?: string;
    visible?: () => boolean;
    convert?: (
        item: T,
        opt: { column: ColumnData<T>; extraData?: any }
    ) => JSX.Element | undefined;
    sortable?: boolean;
    filterable?: boolean;
    customFilter?: (param: { filterValue: any; item: any; row: any }) => boolean;
    customFilterJSX?: (param: {
        column: ColumnData<T>;
        filter: any;
        setFilter: Dispatch<SetStateAction<any>>;
    }) => JSX.Element;
};

export type Columns<T> = { columns: ColumnData<T>[]; };

export type SpecificTypeKey<T, SpecificType> = {
    [K in keyof T]: T[K] extends SpecificType ? K : never
}[keyof T];

export const createReadOnly = <T extends object>(initialValue: T): T => {
    initialValue = new Proxy(deepCopy(initialValue), {
        set(_, prop, value) {
            const propName =
                typeof prop === "string" && !isNaN(Number(prop))
                    ? `[${prop}]`
                    : `.${String(prop)}`;

            console.error(
                new Error(
                    `Objeto imutável. Não é possível setar '${value}' em 'immutableObject${propName}'.`
                )
            );

            return false;
        },
    });
    return Object.freeze(initialValue);
};

export const isAbertoOuParcial = (status: StatusContaPagarReceber) => {
    return status === StatusContaPagarReceber.ABERTA || status === StatusContaPagarReceber.PARCIAL
}

export const isParcialOuBaixado = (status: StatusContaPagarReceber) => {
    return status === StatusContaPagarReceber.PARCIAL || status === StatusContaPagarReceber.BAIXADA
}

export const isChecked = <T extends { uuid: string } | string>(
    array: T[],
    item: T
): boolean => {
    const hasUUID = (item: unknown): item is { uuid: string } => {
        return !!item && typeof item === "object" && "uuid" in item;
    };

    if (hasUUID(item)) {
        return array.some((e) => hasUUID(e) && e.uuid === item.uuid);
    }
    return array.includes(item);
};

export const resolveWrapper = (data: any, key: string) => resolve(data, key);


/**
 * Extrai as chaves obrigatórias de um tipo `T`,
 * ou seja, aquelas que **não** são `undefined`.
 *
 * @template T - O tipo base.
 */
export type RequiredKeys<T> = {
    [K in keyof T]: undefined extends T[K] ? never : K;
}[keyof T];

/**
 * Extrai as chaves opcionais de um tipo `T`,
 * ou seja, aquelas que **podem ser** `undefined`.
 *
 * @template T - O tipo base.
 */
export type OptionalKeys<T> = {
    [K in keyof T]: undefined extends T[K] ? K : never;
}[keyof T];

/**
 * Transforma determinadas propriedades de um tipo `T` em opcionais.
 *
 * @template T - O tipo base.
 * @template K - Conjunto de chaves de `T` que serão transformadas em opcionais.
 */
export type MakePropsOptional<T, K extends RequiredKeys<T>> = Omit<T, K> &
    Partial<Pick<T, K>>;

/**
 * Transforma determinadas propriedades de um tipo `T` em obrigatórias.
 *
 * @template T - O tipo base.
 * @template K - Conjunto de chaves de `T` que serão transformadas em obrigatórias.
 */
export type MakePropsRequired<T, K extends OptionalKeys<T>> = Omit<T, K> &
    Required<Pick<T, K>>;

/**
 * Transforma simultaneamente um grupo de propriedades opcionais de `T` em obrigatórias (`R`)
 * e um grupo de propriedades obrigatórias em opcionais (`O`).
 *
 * - `O` deve conter apenas chaves **obrigatórias** (sem `undefined`) de `T`;
 * - `R` deve conter apenas chaves **opcionais** (com `undefined`) de `T`.
 *
 * @template T - O tipo original.
 * @template O - Chaves que devem ser tornadas opcionais.
 * @template R - Chaves que devem ser tornadas obrigatórias.
 *
 * @example
 * interface Todo {
 *   completed: boolean;
 *   description: string;
 *   title?: string;
 * }
 *
 * const abc: Todo = {
 *   completed: true,
 *   description: "description", // obrigatório
 *   //   title: "title", // opcional
 * };
 *
 * const def: ToggleRequiredProps<Todo, "description", "title"> = {
 *   completed: true,
 *   //   description: "description", // opcional
 *   title: "title", // obrigatório
 * };
 */
export type ToggleRequiredProps<
    T,
    R extends RequiredKeys<T>,
    O extends OptionalKeys<T>
> = Omit<T, O | R> & Partial<Pick<T, R>> & Required<Pick<T, O>>;


/**
 * Remove `null` (e `undefined`) de todas as propriedades de um tipo,
 * aplicando a transformação de forma recursiva.
 *
 * - Propriedades que aceitam `null` terão `null` removido.
 * - Propriedades que são exatamente `null` serão tratadas como `never`.
 * - Propriedades com objetos ou arrays serão processadas recursivamente.
 *
 * @template T Tipo de entrada que pode conter propriedades `null` ou `undefined`.
 * @returns Um novo tipo com todas as ocorrências de `null` removidas, inclusive de propriedades aninhadas.
 *
 * @example
 * type Entrada = {
 *   nome: string | null;
 *   idade: number | null;
 *   endereco: {
 *     rua: string | null;
 *     numero: number;
 *   } | null;
 *   tags: string[] | null;
 * };
 *
 * type Resultado = RemoveNull<Entrada>;
 * // Resultado:
 * // {
 * //   nome: string;
 * //   idade: number;
 * //   endereco: {
 * //     rua: string;
 * //     numero: number;
 * //   };
 * //   tags: string[];
 * // }
 */
export type RemoveNull<T> = {
    [K in keyof T]: T[K] extends null
    ? never
    : T[K] extends object
    ? RemoveNull<NonNullable<T[K]>>
    : NonNullable<T[K]>;
};

// O VoValorMonetario do nexus valida no máximo 21 dígitos e o back-end normaliza o valor com
// 10 casas decimais, então sobram 11 dígitos para a parte inteira. Teto com folga para o front
// não enviar valor que estoure a validação (ver FLW-1126).
export const VALOR_MONETARIO_MAXIMO = 9_999_999_999.99;
